import { ReactNode, useState } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Warning } from '@phosphor-icons/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import * as yup from 'yup';
import {
  ExclamationOctagonIcon,
  Link,
  Loader,
  Select,
  SelectOption,
  Typography,
} from '@la/ds-ui-components';
import { getSiteId, getUserId, useAppSelector } from '@la/services';
import { WizardContent } from '@la/shared-components/src/tournaments/registration/Wizard/Wizard.styles';
import {
  DivisionsAndTeamsOptions,
  RegisteredRegistrationType,
  RegistrationAtCapacityType,
  RegistrationDecodedParams,
  RegistrationType,
  Tournament,
} from '@la/types';
import { capitalizeFirstLetter, getLAHostnameParts } from '@la/utilities';
import { Alert } from 'components/Alert/Alert';
import ErrorCard from 'components/ErrorCard/ErrorCard';
import { Stepper } from 'components/Stepper/Stepper';
import { clearLocalStorage } from 'lib/context/RegistrationContext/RegistrationContext.utils';
import { useRegistrationAddOns } from 'redux/hooks/useRegistrationAddOns';
import { useGetSelectRegistrationQuery } from 'redux/services/registrationApi';
import { DiscardChangesModal } from 'domains/RosterManagement/RosterRollover/DiscardChangesModal/DiscardChangesModal';
import MainContent from 'domains/Shell/MainContent/MainContent';
import { Alert as WarningAlert } from 'domains/Tournaments/Alert/Alert';
import { API_ERROR_MESSAGE } from '../Registration/Registration';
import { RegistrationHeader } from '../Registration/RegistrationHeader/RegistrationHeader';
import { WizardHeader } from '../Registration/Wizard/components/WizardHeader/WizardHeader';
import { RegistrationTypeItem } from './RegistrationTypeItem/RegistrationTypeItem';
import styles from './SelectRegistrationType.module.scss';
import { SelectRegistrationFields } from './SelectRegistrationType.types';
import {
  additionalInfoFields,
  getAtCapacityMessage,
  registrationTypesAdditionalInfoTitle,
  registrationTypesSubtitles,
  registrationTypesTitles,
} from './SelectRegistrationType.utils';

const DEFAULT_FIELD_VALUES: SelectRegistrationFields = {
  selectedDivision: '',
  selectedType: RegistrationType.Team,
  selectedStaffRole: '',
  selectedTeam: '',
};
const ADDITIONAL_INFO_SECTION_ID = 'additional-info-section';
// Enums are weird to iterate through, especially narrowing down to Enums dictionary where
// the key and definition are the same. eg: typeof enum === enum[string].
const registrationTypesValues: RegisteredRegistrationType[] = [
  RegistrationType.Team,
  RegistrationType.Player,
  RegistrationType.PlayerFreeAgent,
  RegistrationType.Staff,
  RegistrationType.StaffFreeAgent,
];

const baseSchema = yup.object({
  selectedType: yup.string().required(),
  selectedDivision: yup.string().when('selectedType', {
    is: (role: string) => {
      return role !== RegistrationType.Team;
    },
    then: () => yup.string().required('Please select a program.'),
    otherwise: () => yup.string(),
  }),
  selectedTeam: yup.string().when('selectedType', {
    is: (role: RegisteredRegistrationType) => {
      return additionalInfoFields[role]?.includes('selectedTeam');
    },
    then: () => yup.string().required('Please select a team.'),
    otherwise: () => yup.string(),
  }),
  selectedStaffRole: yup.string().when('selectedType', {
    is: RegistrationType.Staff,
    then: () => yup.string().required('Please select a role.'),
    otherwise: () => yup.string(),
  }),
});

const getRegistrantData = (
  selectedType: RegisteredRegistrationType | null,
  state: {
    selectedDivision: string;
    siteId: string | null;
    selectedTeam?: string;
    selectedStaffRole?: string;
  }
) => {
  const { selectedDivision, siteId, selectedTeam, selectedStaffRole } = state;

  const baseRegistrationData = {
    prid: selectedDivision,
    site: siteId,
  };

  switch (selectedType) {
    case RegistrationType.Player: {
      return {
        ...baseRegistrationData,
        role: 'player',
        type: RegistrationType.Player,
        team: selectedTeam,
      };
    }
    case RegistrationType.PlayerFreeAgent: {
      return {
        ...baseRegistrationData,
        role: 'player',
        type: RegistrationType.PlayerFreeAgent,
      };
    }
    case RegistrationType.Staff: {
      return {
        ...baseRegistrationData,
        role: selectedStaffRole,
        type: RegistrationType.Staff,
        team: selectedTeam,
      };
    }
    case RegistrationType.StaffFreeAgent: {
      return {
        ...baseRegistrationData,
        role: selectedStaffRole ? selectedStaffRole : undefined,
        type: RegistrationType.StaffFreeAgent,
      };
    }
  }
};

export const SelectRegistrationType = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<RegisteredRegistrationType>(
    RegistrationType.Team
  );
  const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);

  const { setRegistrationType } = useRegistrationAddOns();

  const { tournamentId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const atCapacityType = queryParams.get('atCapacity');
  let atCapacityMessage: ReactNode | string =
    getAtCapacityMessage(atCapacityType);
  if (atCapacityType === RegistrationAtCapacityType.All) {
    atCapacityMessage = (
      <span>
        {atCapacityMessage}{' '}
        <Link href="/dashboard" size="large">
          View dashboard
        </Link>
      </span>
    );
  }
  const userId = useAppSelector(getUserId);
  const siteId = useAppSelector(getSiteId);

  if (!tournamentId) {
    throw new Error('The tournament ID is not defined');
  }

  const { subdomain: siteDomain } = getLAHostnameParts();

  const methods = useForm<SelectRegistrationFields>({
    defaultValues: DEFAULT_FIELD_VALUES,
    resolver: yupResolver(baseSchema),
  });

  const {
    watch,
    formState: { errors },
    setValue,
    reset,
    trigger,
    getFieldState,
  } = methods;

  const selectedTeam = watch('selectedTeam');
  const selectedStaffRole = watch('selectedStaffRole');
  const selectedDivision = watch('selectedDivision');

  const handleRoleChange = (type: RegisteredRegistrationType | null) => {
    if (type) {
      reset();
      setSelectedType(type);
      setValue('selectedType', type);
    }
    if (type && type !== RegistrationType.Team) {
      setTimeout(() => {
        window.scrollTo({ behavior: 'smooth', top: 1200 });
      }, 40);
    }
  };

  const {
    data,
    isError: tournamentError,
    isLoading,
  } = useGetSelectRegistrationQuery(
    {
      siteDomain,
      tournamentId,
      userId: userId.toString(),
    },
    {
      skip: !userId || !tournamentId || !siteDomain,
    }
  );

  if (isLoading || (!data && !tournamentError)) {
    return (
      <MainContent centerContent={true}>
        <Loader description="We are gathering registration data..." loading />
      </MainContent>
    );
  }

  if (tournamentError) {
    return (
      <MainContent>
        <WizardContent>
          <ErrorCard message={API_ERROR_MESSAGE} />
        </WizardContent>
      </MainContent>
    );
  }

  if (!data) {
    throw new Error('No data found for the tournament.');
  }

  const {
    divisionsAndTeamsOptions,
    divisionsFees,
    priceRanges,
    staffRolesOptions,
    tournament,
  } = data;

  const navigateToMemberRegistration = (data: RegistrationDecodedParams) => {
    clearLocalStorage([
      'lip.decodedParams',
      'lip.invite-token',
      'lip.registrationId',
      'selectedPlayer',
    ]);
    const encodedData = window.btoa(JSON.stringify(data));
    const path = `/app/registration`;
    navigate(`${path}?selectRegistrationData=${encodedData}`);
  };

  const handleNextClick = () => {
    if (selectedType === RegistrationType.Team) {
      navigate(`/app/tournaments/${tournamentId}/registration`);
      return;
    }

    setRegistrationType(selectedType);

    const registrantData = getRegistrantData(selectedType, {
      selectedDivision,
      siteId,
      selectedTeam,
      selectedStaffRole,
    });

    if (!registrantData) {
      console.error('Invalid selected type');
      return;
    }

    navigateToMemberRegistration(registrantData);
  };

  const onSubmit = () => {
    trigger().then(() => {
      if (!selectedType) {
        return;
      }
      const hasErrors = additionalInfoFields[selectedType]
        .map((key) => {
          return getFieldState(key as keyof SelectRegistrationFields).invalid;
        })
        .some((invalid) => invalid);
      if (!hasErrors) {
        handleNextClick();
      }
    });
  };

  const getRegistrationWindow = () => {
    let divisionsStartDate: DateTime | undefined;
    let divisionsEndDate: DateTime | null = null;

    tournament.divisions.forEach((division) => {
      if (
        divisionsStartDate === undefined ||
        DateTime.fromISO(division.startDate) < divisionsStartDate
      ) {
        divisionsStartDate = DateTime.fromISO(division.startDate);
      }

      if (
        division.endDate !== null &&
        division.endDate !== undefined &&
        (divisionsEndDate === null ||
          DateTime.fromISO(division.endDate) > divisionsEndDate)
      ) {
        divisionsEndDate = DateTime.fromISO(division.endDate);
      }
    });

    return { divisionsStartDate, divisionsEndDate };
  };

  const navigateToTournamentPage = () => {
    navigate(`/app/tournaments/${tournament.id}`);
  };

  const { divisionsStartDate, divisionsEndDate } = getRegistrationWindow();

  const getRegistrationTypeDisabledState = (
    registrationType: RegisteredRegistrationType
  ) => {
    // Check if the registration type is enabled in at least one division
    if (
      !tournament.divisions
        .map((division) => division.id)
        .some((id) => divisionsFees[id][registrationType].enabled)
    ) {
      return true;
    }

    // For team registration, we only need to check if it's enabled (no additional validation needed)
    if (registrationType === RegistrationType.Team) {
      return false;
    }

    const divisions = divisionsAndTeamsOptions[registrationType];
    if (
      divisions.length === 0 ||
      divisions.every((division) => division.disabled)
    ) {
      return true;
    }

    return (
      additionalInfoFields[registrationType].includes('selectedTeam') &&
      // Check that there are any team options available
      divisionsAndTeamsOptions[registrationType].every(
        (option) =>
          !option.teamsOptions?.length ||
          option.teamsOptions?.every((option) => option.disabled)
      )
    );
  };

  const enabledRegistrationTypes = registrationTypesValues.filter(
    (type) => !getRegistrationTypeDisabledState(type)
  );

  return (
    <FormProvider {...methods}>
      <MainContent>
        <RegistrationPageHeader
          divisionsEndDate={divisionsEndDate}
          divisionsStartDate={divisionsStartDate}
          tournament={tournament}
        />
        <Stepper
          currentStep={{
            longDesc: 'Select a registration type',
            shortDesc: '',
          }}
          error={false}
          form={{}}
          handleNextClick={onSubmit}
          numberOfTotalSteps={2}
          onBackClick={() => {}}
          showDrawer={true}
          stepNumber={1}
          showSteps={false}
          showBackButton={false}
          type="button"
          onCancelButtonClick={() => {
            setShowDiscardChangesModal(true);
          }}
        >
          <form className={styles.selectRegistrationContainer}>
            <Typography variant="headline" size="medium" weight="bold">
              Select a registration type
            </Typography>
            {atCapacityType && atCapacityMessage ? (
              <WarningAlert
                icon={<Warning weight="bold" />}
                message={atCapacityMessage}
              />
            ) : null}
            {errors.selectedType ? (
              <Alert
                message="Please select a registration type before continuing."
                icon={
                  <ExclamationOctagonIcon
                    size="xl"
                    variant="bold"
                    fill="var(--red-500)"
                  />
                }
              />
            ) : null}
            <div className={styles.selectionSection}>
              {enabledRegistrationTypes.map((registrationType) => (
                <RegistrationTypeItem
                  costLabel={tournament.costLabel}
                  disabled={getRegistrationTypeDisabledState(registrationType)}
                  key={registrationType}
                  fullWidth={enabledRegistrationTypes.length === 1}
                  price={priceRanges[registrationType]}
                  selectedType={selectedType}
                  setSelectedType={handleRoleChange}
                  subtitle={registrationTypesSubtitles[registrationType]}
                  title={registrationTypesTitles[registrationType]}
                  type={registrationType}
                />
              ))}
            </div>
            <div id={ADDITIONAL_INFO_SECTION_ID}>
              <RoleAdditionalInfo
                divisionsAndTeamsOptions={
                  selectedType ? divisionsAndTeamsOptions[selectedType] : []
                }
                selectedType={selectedType}
                selectedTeam={selectedTeam}
                selectedDivision={selectedDivision}
                selectedStaffRole={selectedStaffRole}
                staffRolesOptions={staffRolesOptions}
                subProgramLabel={data.tournament.subProgramLabel}
              />
            </div>
          </form>
        </Stepper>
        {showDiscardChangesModal ? (
          <DiscardChangesModal
            onDiscardChangesClick={navigateToTournamentPage}
            onOpenChange={setShowDiscardChangesModal}
            open={showDiscardChangesModal}
          />
        ) : null}
      </MainContent>
    </FormProvider>
  );
};

function RegistrationPageHeader({
  divisionsEndDate,
  divisionsStartDate,
  tournament,
}: {
  divisionsEndDate: DateTime | null;
  divisionsStartDate: DateTime | undefined;
  tournament: Tournament;
}) {
  const { showUpdatedECommerceInRegFlowContent } = useFlags();
  const endDate = divisionsEndDate ?? undefined;
  const startDate =
    divisionsStartDate || DateTime.fromISO(tournament.startDate);
  const tournamentLocation = !tournament.hideLocationDetails
    ? tournament.location?.name
    : undefined;

  return showUpdatedECommerceInRegFlowContent ? (
    <RegistrationHeader
      tournamentEndDate={endDate}
      tournamentLocation={tournamentLocation}
      tournamentName={tournament.name}
      tournamentStartDate={startDate}
    />
  ) : (
    <WizardHeader
      endDate={endDate}
      location={tournamentLocation}
      name={tournament.name}
      startDate={startDate}
    />
  );
}

const formatSpotsLeftText = (spotsLeft: number): string => {
  return `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`;
};

const getSpotsLeftText = (
  spotsLeft?: string | undefined
): string | ReactNode | undefined => {
  if (!spotsLeft) {
    return;
  }
  const spotsLeftNum = parseInt(spotsLeft);
  if (isNaN(spotsLeftNum)) {
    return;
  }

  const spotsLeftText = formatSpotsLeftText(spotsLeftNum);
  if (spotsLeftNum === 0) {
    return 'At capacity';
  } else if (spotsLeftNum <= 5) {
    return (
      <span className={styles.spotsRemainingWarning}>{spotsLeftText}</span>
    );
  }
  return spotsLeftText;
};

const applySelectOptionSpotsLeftSubLabel = (
  option: SelectOption
): SelectOption => {
  const { subLabel: spotsLeft, ...restOption } = option;
  const subLabel = getSpotsLeftText(spotsLeft?.toString());
  return { ...restOption, subLabel };
};

const RoleAdditionalInfo = ({
  divisionsAndTeamsOptions,
  selectedType,
  selectedDivision,
  staffRolesOptions,
  subProgramLabel,
}: {
  divisionsAndTeamsOptions: DivisionsAndTeamsOptions;
  selectedType: RegisteredRegistrationType | null;
  selectedDivision: string;
  selectedTeam: string;
  staffRolesOptions: SelectOption[];
  selectedStaffRole: string;
  subProgramLabel: string;
}) => {
  const { setValue, control, clearErrors } =
    useFormContext<SelectRegistrationFields>();

  if (!selectedType || selectedType === RegistrationType.Team) {
    return null;
  }

  const requiredFields = additionalInfoFields[selectedType];

  const divisionOptions: SelectOption[] = divisionsAndTeamsOptions.map(
    applySelectOptionSpotsLeftSubLabel
  );
  const teamOptions = (
    divisionsAndTeamsOptions.find((option) => option.value === selectedDivision)
      ?.teamsOptions ?? []
  ).map(applySelectOptionSpotsLeftSubLabel);

  return (
    <div className={styles.additionalInfoSection}>
      <Typography variant="headline" size="small" weight="bold">
        {registrationTypesAdditionalInfoTitle[selectedType]}
      </Typography>
      <div className={styles.additionalInfoContent}>
        {requiredFields.includes('selectedDivision') ? (
          <Controller
            control={control}
            name="selectedDivision"
            render={({ field, fieldState }) => {
              return (
                <div className={styles.additionalInfoDivisions}>
                  <Select
                    {...field}
                    id="selectedDivision"
                    label={capitalizeFirstLetter(subProgramLabel)}
                    options={divisionOptions}
                    onChange={(divisionId) => {
                      setValue('selectedDivision', divisionId);
                      if (field.value !== divisionId) {
                        setValue('selectedTeam', '');
                      }
                      clearErrors('selectedDivision');
                    }}
                    placeholder={`Select a ${subProgramLabel}`}
                    required
                    width="100%"
                    hasError={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                  />
                </div>
              );
            }}
          />
        ) : null}
        {requiredFields.includes('selectedTeam') ? (
          <Controller
            control={control}
            name="selectedTeam"
            render={({ field, fieldState }) => {
              return (
                <div className={styles.additionalInfoTeams}>
                  <Select
                    {...field}
                    id="selectedTeam"
                    label="Team"
                    placeholder="Select a team"
                    options={teamOptions}
                    onChange={(teamId) => {
                      setValue('selectedTeam', teamId);
                      clearErrors('selectedTeam');
                    }}
                    required
                    disabled={!selectedDivision}
                    width="100%"
                    hasError={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                  />
                </div>
              );
            }}
          />
        ) : null}
        {requiredFields.includes('selectedStaffRole') ? (
          <Controller
            control={control}
            name="selectedStaffRole"
            render={({ field, fieldState }) => {
              return (
                <div className={styles.additionalInfoRoles}>
                  <Select
                    {...field}
                    id="selectedStaffRole"
                    placeholder="Select a role"
                    label="Your role"
                    options={staffRolesOptions}
                    onChange={(roleId) => {
                      setValue('selectedStaffRole', roleId);
                      clearErrors('selectedStaffRole');
                    }}
                    required={selectedType === RegistrationType.Staff}
                    optional={selectedType === RegistrationType.StaffFreeAgent}
                    hasError={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                  />
                </div>
              );
            }}
          />
        ) : null}
      </div>
    </div>
  );
};
